import type { EconDictName } from '@entities/economic/dictionaryTypes';

/** Тип поля формы справочника. */
export type FieldType = 'text' | 'number' | 'checkbox' | 'select' | 'date';

export interface FieldDef {
  name: string;
  label: string;
  type: FieldType;
  required?: boolean;
  options?: { value: string; label: string }[];
  placeholder?: string;
  hint?: string;
  /** Значение новой записи. Подсказку (`placeholder`) люди принимают за
   *  введённое значение: «м3» в пустом поле единицы лимита не сохранялось. */
  defaultValue?: string;
}

/** Схемы полей справочников с типизированными формами (без profit_split — у него
 *  своя мультистрочная форма). Поля соответствуют NewEcon*-DTO. */
export const DICT_FIELDS: Record<Exclude<EconDictName, 'profit_split'>, FieldDef[]> = {
  objects: [
    { name: 'code', label: 'Код', type: 'text', required: true, placeholder: 'ОЗХ-1' },
    { name: 'name_1c', label: 'Наименование в 1С', type: 'text' },
    { name: 'name_plan', label: 'Наименование в плане', type: 'text' },
    { name: 'name_canonical', label: 'Каноничное наименование', type: 'text' },
    { name: 'project_id', label: 'Проект ПТО (сквозная связка)', type: 'select', options: [] },
    {
      name: 'is_internal',
      label: 'Свой объект (получатель внутреннего отпуска, Р-03/Р-04)',
      type: 'checkbox',
    },
  ],
  cost_articles: [
    { name: 'code', label: 'Код', type: 'text', required: true },
    { name: 'name', label: 'Наименование', type: 'text', required: true },
    {
      name: 'direction',
      label: 'Направление',
      type: 'select',
      required: true,
      options: [
        { value: 'revenue', label: 'Доход' },
        { value: 'cost_direct', label: 'Прямые затраты' },
        { value: 'cost_indirect', label: 'Косвенные затраты' },
        { value: 'aup', label: 'АУП' },
        { value: 'other', label: 'Прочее' },
      ],
    },
  ],
  cashflow_articles: [
    { name: 'code', label: 'Код', type: 'text', required: true },
    { name: 'name', label: 'Наименование', type: 'text', required: true },
    {
      name: 'flow_type',
      label: 'Тип потока',
      type: 'select',
      required: true,
      options: [
        { value: 'inflow', label: 'Приток' },
        { value: 'outflow', label: 'Отток' },
      ],
    },
  ],
  // Обязателен только номер: лицензию заводят по номеру и сроку, лимиты и
  // нормативы дописывают позже.
  licenses: [
    {
      name: 'license_no',
      label: 'Номер лицензии',
      type: 'text',
      required: true,
      hint: 'Как в самой лицензии, с серией и видом. По номеру расчёт добычи находит лицензию.',
    },
    { name: 'field_name', label: 'Месторождение', type: 'text' },
    {
      name: 'mineral',
      label: 'Полезное ископаемое',
      type: 'text',
      placeholder: 'песок',
      hint: 'Не указано — «песок».',
    },
    { name: 'annual_limit_m3', label: 'Годовой лимит, м³', type: 'number' },
    { name: 'approved_reserves_m3', label: 'Утверждённые запасы, м³', type: 'number' },
    { name: 'opening_balance_m3', label: 'Остаток на начало года, м³', type: 'number' },
    { name: 'valid_from', label: 'Действует с', type: 'date' },
    { name: 'valid_to', label: 'Действует по', type: 'date' },
    {
      name: 'density_t_to_m3',
      label: 'Плотность т→м³',
      type: 'number',
      hint: 'коэффициент перевода тонн в м³ (Р-05)',
    },
    { name: 'loss_rate_pct', label: 'Норматив потерь, %', type: 'number' },
    {
      name: 'limit_unit',
      label: 'Единица лимита',
      type: 'select',
      options: [
        { value: 'm3', label: 'м³' },
        { value: 't', label: 'т' },
      ],
      defaultValue: 'm3',
    },
  ],
  // Поля — ровно те, что принимает API (`UnitRatePayload`): раньше форма слала
  // `unit_price`, `category`, `contract_ref`, и цена молча терялась.
  unit_rates: [
    {
      name: 'code',
      label: 'Код расценки',
      type: 'text',
      required: true,
      placeholder: 'ФЕР01-01-001-01',
    },
    { name: 'name', label: 'Наименование работы / ресурса', type: 'text', required: true },
    { name: 'unit', label: 'Единица измерения', type: 'text', placeholder: 'м3' },
    {
      name: 'contract_text',
      label: 'Договор',
      type: 'text',
      placeholder: 'Общий справочник',
      hint: 'Номер договора текстом. Пусто — «Общий справочник».',
    },
    { name: 'materials_per_unit', label: 'Материалы на единицу, ₽', type: 'number' },
    { name: 'man_hours_per_unit', label: 'Трудозатраты, чел.-ч на единицу', type: 'number' },
    { name: 'labor_rate', label: 'Ставка оплаты труда, ₽/чел.-ч', type: 'number' },
    { name: 'machine_hours_per_unit', label: 'Машины, маш.-ч на единицу', type: 'number' },
    { name: 'machine_rate', label: 'Ставка машины, ₽/маш.-ч', type: 'number' },
    {
      name: 'machinist_rate',
      label: 'Ставка механизатора, ₽/ч',
      type: 'number',
      placeholder: '660',
    },
    {
      name: 'overhead_pct',
      label: 'Накладные расходы, доля',
      type: 'number',
      placeholder: '0,20',
      hint: '0,20 — это 20 %',
    },
    {
      name: 'profit_pct',
      label: 'Сметная прибыль, доля',
      type: 'number',
      placeholder: '0,15',
      hint: '0,15 — это 15 %',
    },
  ],
};

/** Справочники со свободным правилом классификации (match_rule, JSON-объект). */
export const HAS_MATCH_RULE = new Set<EconDictName>(['cost_articles', 'cashflow_articles']);

/** Формат правила по справочнику: условия у статей БДР и ДДС разные, и правило
 *  с чужими условиями не сработает (статьи БДР сервер такое не примет). */
export const MATCH_RULE_HINTS: Partial<Record<EconDictName, { text: string; example: string }>> = {
  cost_articles: {
    text:
      'Условия: keyword — слова из статьи затрат документа (достаточно одного), ' +
      'counterparty_pattern — часть названия контрагента, 1c_account — счёт 1С (начало ' +
      'счёта). Заданные условия должны выполниться все; строка, подошедшая под правило, ' +
      'относится к этой статье раньше распознавания.',
    example: '{"keyword":["аренда","спецтехника"],"1c_account":["20","26"]}',
  },
  cashflow_articles: {
    text:
      'Условия: counterparty — часть названия контрагента, purpose_pattern — часть ' +
      'назначения платежа.',
    example: '{"purpose_pattern":"аренда"}',
  },
};

export type FormValues = Record<string, string | boolean>;

/** Начальные значения формы из схемы (чекбокс → false, прочее → значение по
 *  умолчанию или ''). */
export function emptyValues(fields: FieldDef[]): FormValues {
  const v: FormValues = {};
  for (const f of fields) v[f.name] = f.type === 'checkbox' ? false : (f.defaultValue ?? '');
  return v;
}

/** Значения формы из существующей записи (для редактирования). */
export function valuesFromRecord(fields: FieldDef[], rec: Record<string, unknown>): FormValues {
  const v: FormValues = {};
  for (const f of fields) {
    const raw = rec[f.name];
    if (f.type === 'checkbox') v[f.name] = raw === true;
    else if (raw === null || raw === undefined) v[f.name] = '';
    else v[f.name] = String(raw);
  }
  return v;
}

/** Собрать DTO-тело из значений формы + валидация. Ошибка → `{ error }`. */
export function buildBody(
  fields: FieldDef[],
  values: FormValues,
  matchRuleJson: string,
): { body?: Record<string, unknown>; error?: string } {
  const body: Record<string, unknown> = {};
  for (const f of fields) {
    const raw = values[f.name];
    if (f.type === 'checkbox') {
      body[f.name] = raw === true;
      continue;
    }
    const s = typeof raw === 'string' ? raw.trim() : '';
    if (s === '') {
      if (f.required) return { error: `Заполните поле «${f.label}».` };
      continue; // необязательное пустое — не отправляем (backend подставит default/null)
    }
    if (f.type === 'number') {
      // «150 000» и «0,7» — как пишут люди: пробелы разрядов и запятая.
      const n = Number(s.replace(/\s/g, '').replace(',', '.'));
      if (!Number.isFinite(n)) return { error: `Поле «${f.label}» должно быть числом.` };
      body[f.name] = n;
    } else {
      body[f.name] = s;
    }
  }
  const rule = matchRuleJson.trim();
  if (rule) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(rule);
    } catch {
      return { error: 'Правило классификации (match_rule): невалидный JSON.' };
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return { error: 'Правило классификации (match_rule) должно быть JSON-объектом.' };
    }
    body.match_rule = parsed;
  }
  return { body };
}

/** Одна строка формы распределения прибыли: объект + доля в процентах. */
export interface SplitRow {
  objectId: string;
  pct: string;
}

/** Доля 0..1 → проценты строкой без хвоста двоичной арифметики (0,07 → «7»). */
export function weightToPct(weight: number): string {
  return String(Math.round(weight * 1e6) / 1e4);
}

/** Ставки НДС — общий с ОСНОВА.Сметчик справочник (миграция 0225), поэтому он
 *  не входит в `ECON_DICTIONARIES` (там справочники, принадлежащие econ) и
 *  живёт отдельной вкладкой с собственным экраном. */
export const VAT_TAB = 'vat_rates' as const;

// ── Экран справочника: таблица и боковая панель записи ───────────────────

/** Справочник с таблицей и панелью записи (у распределения прибыли — своя форма). */
export type TableDict = Exclude<EconDictName, 'profit_split'>;

export type DictRecord = Record<string, unknown>;

/** Поле во всю ширину панели: длинные названия, связка, флажок. */
const WIDE_FIELDS = new Set([
  'license_no',
  'name',
  'name_1c',
  'name_plan',
  'name_canonical',
  'project_id',
  'contract_text',
]);

export function isWideField(f: FieldDef): boolean {
  return f.type === 'checkbox' || WIDE_FIELDS.has(f.name);
}

function text(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

/** Число по-русски: «150 000», «0,7»; нет — «—». */
export function formatNumber(v: unknown): string {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN;
  return Number.isFinite(n) ? n.toLocaleString('ru-RU', { maximumFractionDigits: 4 }) : '—';
}

/** Дата `ГГГГ-ММ-ДД` → «ДД.ММ.ГГГГ»; нет — пусто. */
export function formatDay(v: unknown): string {
  const [y, m, d] = text(v).slice(0, 10).split('-');
  return y && m && d ? `${d}.${m}.${y}` : '';
}

/** Срок действия: «01.03.2021 — 28.02.2041», «с 01.03.2021», «по …» или «—». */
export function termText(from: unknown, to: unknown): string {
  const a = formatDay(from);
  const b = formatDay(to);
  if (a && b) return `${a} — ${b}`;
  if (a) return `с ${a}`;
  if (b) return `по ${b}`;
  return '—';
}

/** Правило классификации задано: непустой JSON-объект. */
function hasRule(v: unknown): boolean {
  return typeof v === 'object' && v !== null && !Array.isArray(v) && Object.keys(v).length > 0;
}

function optionLabel(dict: TableDict, field: string, value: unknown): string {
  const f = DICT_FIELDS[dict].find((x) => x.name === field);
  return f?.options?.find((o) => o.value === value)?.label ?? text(value);
}

/** Колонка таблицы справочника. */
export interface DictColumn {
  key: string;
  title: string;
  cell: (rec: DictRecord, ctx: ColumnContext) => string;
  /** Строка мелким шрифтом под значением. */
  sub?: (rec: DictRecord, ctx: ColumnContext) => string | null;
  /** Число — вправо, табличные цифры. */
  num?: boolean;
  /** Ключ записи: им открывается панель записи. */
  primary?: boolean;
}

export interface ColumnContext {
  /** Название проекта ПТО по id (связка объекта). */
  projectName: (id: string) => string | null;
}

/** Подписи и колонки справочника. */
export interface DictUi {
  /** «объект», «статью БДР» — после «Добавить». */
  accusative: string;
  /** Заголовок панели новой записи. */
  newTitle: string;
  /** Заголовок панели записи: «Лицензия «ТЮМ 01234 ТЭ»». */
  recordTitle: (rec: DictRecord) => string;
  /** Род для «Выключен(а)». */
  feminine: boolean;
  columns: DictColumn[];
  /** Пустой справочник: что в нём будет и зачем. */
  emptyText: string;
}

/** Текст поля записи или «—». */
const textOr = (rec: DictRecord, key: string) => text(rec[key]) || '—';

export const DICT_UI: Record<TableDict, DictUi> = {
  objects: {
    accusative: 'объект',
    newTitle: 'Новый объект',
    recordTitle: (r) => `Объект «${text(r.code)}»`,
    feminine: false,
    emptyText:
      'Объекты учёта связывают названия из 1С, плана и ПТО: по ним расходы и доходы разносятся по объектам.',
    columns: [
      { key: 'code', title: 'Код', cell: (r) => textOr(r, 'code'), primary: true },
      {
        key: 'name',
        title: 'Наименование',
        cell: (r) => text(r.name_canonical) || text(r.name_plan) || text(r.name_1c) || '—',
        sub: (r) => {
          const c1 = text(r.name_1c);
          const shown = text(r.name_canonical) || text(r.name_plan) || c1;
          return c1 && c1 !== shown ? `в 1С: ${c1}` : null;
        },
      },
      {
        key: 'project',
        title: 'Проект ПТО',
        cell: (r, ctx) => (text(r.project_id) && ctx.projectName(text(r.project_id))) || '—',
      },
      { key: 'internal', title: 'Свой объект', cell: (r) => (r.is_internal === true ? 'да' : '—') },
    ],
  },
  cost_articles: {
    accusative: 'статью БДР',
    newTitle: 'Новая статья БДР',
    recordTitle: (r) => `Статья БДР «${text(r.code)}»`,
    feminine: true,
    emptyText:
      'Статьи бюджета доходов и расходов: по правилам отнесения строки документов попадают в нужную статью без ручной разноски.',
    columns: [
      { key: 'code', title: 'Код', cell: (r) => textOr(r, 'code'), primary: true },
      { key: 'name', title: 'Наименование', cell: (r) => textOr(r, 'name') },
      {
        key: 'direction',
        title: 'Направление',
        cell: (r) => optionLabel('cost_articles', 'direction', r.direction) || '—',
      },
      { key: 'rule', title: 'Правило', cell: (r) => (hasRule(r.match_rule) ? 'задано' : '—') },
    ],
  },
  cashflow_articles: {
    accusative: 'статью ДДС',
    newTitle: 'Новая статья ДДС',
    recordTitle: (r) => `Статья ДДС «${text(r.code)}»`,
    feminine: true,
    emptyText:
      'Статьи движения денежных средств: по правилу (контрагент, назначение платежа) платёж сам попадает в статью.',
    columns: [
      { key: 'code', title: 'Код', cell: (r) => textOr(r, 'code'), primary: true },
      { key: 'name', title: 'Наименование', cell: (r) => textOr(r, 'name') },
      {
        key: 'flow',
        title: 'Поток',
        cell: (r) => optionLabel('cashflow_articles', 'flow_type', r.flow_type) || '—',
      },
      { key: 'rule', title: 'Правило', cell: (r) => (hasRule(r.match_rule) ? 'задано' : '—') },
    ],
  },
  licenses: {
    accusative: 'лицензию',
    newTitle: 'Новая лицензия',
    recordTitle: (r) => `Лицензия «${text(r.license_no)}»`,
    feminine: true,
    emptyText:
      'Лицензии на недропользование: хватает номера и срока, лимиты и нормативы можно дописать позже. По номеру расчёт добычи находит лицензию.',
    columns: [
      { key: 'no', title: 'Номер', cell: (r) => textOr(r, 'license_no'), primary: true },
      { key: 'field', title: 'Месторождение', cell: (r) => textOr(r, 'field_name') },
      { key: 'mineral', title: 'Полезное ископаемое', cell: (r) => textOr(r, 'mineral') },
      {
        key: 'limit',
        title: 'Годовой лимит',
        num: true,
        cell: (r) => {
          const n = formatNumber(r.annual_limit_m3);
          return n === '—' ? n : `${n} ${r.limit_unit === 't' ? 'т' : 'м³'}`;
        },
      },
      { key: 'term', title: 'Срок действия', cell: (r) => termText(r.valid_from, r.valid_to) },
    ],
  },
  unit_rates: {
    accusative: 'расценку',
    newTitle: 'Новая расценка',
    recordTitle: (r) => `Расценка «${text(r.code)}»`,
    feminine: true,
    emptyText:
      'Единичные расценки ПЭО — эталон для сверки БДР. Их можно завести вручную или импортировать из ЛСР Сметчика.',
    columns: [
      { key: 'code', title: 'Код', cell: (r) => textOr(r, 'code'), primary: true },
      {
        key: 'name',
        title: 'Наименование',
        cell: (r) => textOr(r, 'name'),
        sub: (r) => {
          const c = text(r.contract_text);
          const v =
            typeof r.version_no === 'number' && r.version_no > 1 ? `, версия ${r.version_no}` : '';
          return c ? `${c}${v}` : null;
        },
      },
      { key: 'unit', title: 'Ед.', cell: (r) => textOr(r, 'unit') },
      {
        key: 'materials',
        title: 'Материалы, ₽',
        num: true,
        cell: (r) => formatNumber(r.materials_per_unit),
      },
      {
        key: 'man',
        title: 'Труд, чел.-ч',
        num: true,
        cell: (r) => formatNumber(r.man_hours_per_unit),
      },
      {
        key: 'machine',
        title: 'Машины, маш.-ч',
        num: true,
        cell: (r) => formatNumber(r.machine_hours_per_unit),
      },
    ],
  },
};

/** Подпись состояния записи: действует или выключена. */
export function statusLabel(dict: TableDict, active: boolean): string {
  if (active) return 'Действует';
  return DICT_UI[dict].feminine ? 'Выключена' : 'Выключен';
}

/** Текст строки для поиска: ключ, названия, договор — без регистра и «ё». */
export function searchText(rec: DictRecord): string {
  return [
    rec.code,
    rec.license_no,
    rec.name,
    rec.name_1c,
    rec.name_plan,
    rec.name_canonical,
    rec.field_name,
    rec.mineral,
    rec.contract_text,
  ]
    .map(text)
    .join(' ')
    .toLowerCase()
    .replace(/ё/g, 'е');
}

/** Запрос поиска в том же виде, что [`searchText`]. */
export function searchKey(query: string): string {
  return query.trim().toLowerCase().replace(/ё/g, 'е');
}

/**
 * Ошибки полей формы — под самим полем, а не одной строкой внизу:
 * обязательное пустое и «не число» в числовом поле.
 */
export function fieldIssues(fields: FieldDef[], values: FormValues): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of fields) {
    if (f.type === 'checkbox') continue;
    const raw = values[f.name];
    const s = typeof raw === 'string' ? raw.trim() : '';
    if (s === '') {
      if (f.required) out[f.name] = f.type === 'select' ? 'Выберите значение' : 'Заполните поле';
      continue;
    }
    if (f.type === 'number' && !Number.isFinite(Number(s.replace(/\s/g, '').replace(',', '.')))) {
      out[f.name] = 'Нужно число, например 1500 или 0,7';
    }
  }
  return out;
}

/** Сколько полей формы изменено против исходных значений. */
export function changedCount(initial: FormValues, values: FormValues): number {
  return Object.keys({ ...initial, ...values }).filter(
    (k) => (initial[k] ?? '') !== (values[k] ?? ''),
  ).length;
}
